// Package imports:
import React, { useState, useCallback, useRef, useEffect } from 'react';
// import { faTrashCan } from '@fortawesome/pro-solid-svg-icons';
import { Toast, ToastContainer } from 'react-bootstrap';
import Bugsnag from '@bugsnag/js';

// UI element imports:
import Button from '../../../ui-elements/Button/Button';
import Input from '../../../ui-elements/Input/Input';
// import DropSelect from '../../../ui-elements/DropSelect/DropSelect';
// import Link from '../../../ui-elements/Link/Link';
import GoBackLink from '../../../ui-elements/GoBackLink/GoBackLink';
// import Toggle from '../../../ui-elements/Toggle/Toggle';
import Alert, { AlertType } from '../../../ui-elements/Alert/Alert';
import Modal from '../../../ui-elements/Modal/Modal';

// Service imports:
import { TOAST_DELAY } from '../../../services/config';

// Type imports:
import { ServiceCompanySettings } from '../../../types/DueDiligenceTypes';

// Component imports:
// import KYCSettingsBannerUpload from './KYCSettingsBannerUpload';
// import KYCSettingsEmailPreview from './KYCSettingsEmailPreview';

const FREQUENCY_OPTIONS = [
    { label: '24 mánuðir', value: '24' },
    { label: '18 mánuðir', value: '18' },
    { label: '12 mánuðir', value: '12' },
    { label: '6 mánuðir', value: '6' },
    { label: 'Aldrei', value: '0' },
];

// interface BannerPreview {
//     url: string;
//     name: string;
//     width: number;
//     height: number;
//     size: number;
// }

interface SettingsValues {
    email: string;
    lowRiskFrequency: string | null;
    mediumRiskFrequency: string | null;
    highRiskFrequency: string | null;
    matchRate: string;
    autoLookupEnabled: boolean;
    hasBannerFile: boolean;
}

const DEFAULT_VALUES: SettingsValues = {
    email: '',
    lowRiskFrequency: '24',
    mediumRiskFrequency: '24',
    highRiskFrequency: '24',
    matchRate: '85',
    autoLookupEnabled: false,
    hasBannerFile: false,
};

interface IKYCSettingsProps {
    onGoBack: () => void;
}

const KYCSettings: React.FC<IKYCSettingsProps> = ({ onGoBack }) => {
    // Áminningar
    const [email, setEmail] = useState('');

    // Tíðni áminninga
    const [lowRiskFrequency/*, setLowRiskFrequency*/] = useState<string | null>('24');
    const [mediumRiskFrequency/*, setMediumRiskFrequency*/] = useState<string | null>('24');
    const [highRiskFrequency/*, setHighRiskFrequency*/] = useState<string | null>('24');

    // Alþjóðlegir válistar
    const [matchRate/*, setMatchRate*/] = useState('85');

    // Sjálfvirk uppfletting
    const [autoLookupEnabled/*, setAutoLookupEnabled*/] = useState(false);

    // Banner upload
    const [bannerFile/*, setBannerFile*/] = useState<File | null>(null);
    // const [bannerPreview, setBannerPreview] = useState<BannerPreview | null>(null);
    // const [bannerError, setBannerError] = useState('');

    // Save state
    const [saving, setSaving] = useState(false);

    // Toast state
    const [toastShow, setToastShow] = useState(false);
    const [toastType, setToastType] = useState<AlertType>('success');
    const [toastMessage, setToastMessage] = useState('');

    // Unsaved changes modal
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);

    // Loading state
    const [, setLoading] = useState(true);

    // Track initial/saved values for dirty detection
    const initialValues = useRef<SettingsValues>({ ...DEFAULT_VALUES });

    // Fetch initial settings on mount
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await fetch('/DueDiligence/GetServiceCompanySettings', {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                });
                if (response.ok) {
                    const data: ServiceCompanySettings = await response.json();
                    setEmail(data.email ?? '');
                    initialValues.current = {
                        ...initialValues.current,
                        email: data.email ?? '',
                    };
                }
            } catch (error) {
                Bugsnag.notify(error as Error);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    const getFrequencyLabel = (val: string | null): string => {
        const option = FREQUENCY_OPTIONS.find((o) => o.value === val);
        return option ? option.label : val ?? '–';
    };

    const getChangedFields = useCallback((): { label: string; oldValue: string; newValue: string }[] => {
        const changes: { label: string; oldValue: string; newValue: string }[] = [];
        const init = initialValues.current;
        if (email !== init.email)
            changes.push({ label: 'Netfang', oldValue: init.email || '–', newValue: email || '–' });
        if (lowRiskFrequency !== init.lowRiskFrequency)
            changes.push({ label: 'Tíðni áminninga – lítil áhætta', oldValue: getFrequencyLabel(init.lowRiskFrequency), newValue: getFrequencyLabel(lowRiskFrequency) });
        if (mediumRiskFrequency !== init.mediumRiskFrequency)
            changes.push({ label: 'Tíðni áminninga – meðal áhætta', oldValue: getFrequencyLabel(init.mediumRiskFrequency), newValue: getFrequencyLabel(mediumRiskFrequency) });
        if (highRiskFrequency !== init.highRiskFrequency)
            changes.push({ label: 'Tíðni áminninga – mikil áhætta', oldValue: getFrequencyLabel(init.highRiskFrequency), newValue: getFrequencyLabel(highRiskFrequency) });
        if (matchRate !== init.matchRate)
            changes.push({ label: 'Samsvörun (match rate)', oldValue: `${init.matchRate}%`, newValue: `${matchRate}%` });
        if (autoLookupEnabled !== init.autoLookupEnabled)
            changes.push({ label: 'Sjálfvirk uppfletting', oldValue: init.autoLookupEnabled ? 'Virkt' : 'Óvirkt', newValue: autoLookupEnabled ? 'Virkt' : 'Óvirkt' });
        if ((bannerFile !== null) !== init.hasBannerFile)
            changes.push({ label: 'Borði tölvupósts', oldValue: init.hasBannerFile ? 'Með borða' : 'Enginn borði', newValue: bannerFile !== null ? 'Með borða' : 'Enginn borði' });
        return changes;
    }, [email, lowRiskFrequency, mediumRiskFrequency, highRiskFrequency, matchRate, autoLookupEnabled, bannerFile]);

    const isDirty = getChangedFields().length > 0;

    const handleSave = useCallback(async () => {
        setSaving(true);
        try {
            const settingsPayload: ServiceCompanySettings = {
                email,
            };

            const response = await fetch('/DueDiligence/UpdateServiceCompanySettings', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(settingsPayload),
            });

            if (!response.ok) {
                throw new Error('Failed to save settings');
            }

            // TODO: If bannerFile exists, upload it separately
            // POST /DueDiligence/UploadKYCBanner
            if (bannerFile) {
                console.log('Uploading banner:', bannerFile.name);
            }

            // Reset baseline after successful save
            initialValues.current = {
                email,
                lowRiskFrequency,
                mediumRiskFrequency,
                highRiskFrequency,
                matchRate,
                autoLookupEnabled,
                hasBannerFile: bannerFile !== null,
            };

            setToastType('success');
            setToastMessage('Stillingar vistaðar');
            setToastShow(true);
        } catch (error) {
            Bugsnag.notify(error as Error);
            setToastType('error');
            setToastMessage('Villa kom upp við að vista stillingar');
            setToastShow(true);
        } finally {
            setSaving(false);
        }
    }, [email, lowRiskFrequency, mediumRiskFrequency, highRiskFrequency, matchRate, autoLookupEnabled, bannerFile]);

    // const handleDeleteBanner = useCallback(() => {
    //     setBannerFile(null);
    //     setBannerPreview(null);
    //     setBannerError('');
    //     // TODO: API call to delete banner
    //     // DELETE /DueDiligence/DeleteKYCBanner
    // }, []);

    const handleGoBackClick = useCallback(() => {
        if (isDirty) {
            setShowUnsavedModal(true);
        } else {
            onGoBack();
        }
    }, [isDirty, onGoBack]);

    return (
        <div className="KCL_kyc-settings">
            <div className="shell">
                <div className="kyc-settings__header">
                    <GoBackLink handleGoBackOverride={handleGoBackClick} />
                    <div className="kyc-settings__title-row">
                        <h1>Stillingar</h1>
                        <Button
                            buttonType="primary"
                            onClick={handleSave}
                            showLoader={saving}
                            disabled={saving || !isDirty}
                            className="kyc-settings__save-btn--mobile"
                        >
                            Vista breytingar
                        </Button>
                    </div>
                </div>

                <div className="kyc-settings__columns">
                    {/* Left column */}
                    <div className="kyc-settings__column kyc-settings__column--left">
                        {/* Áminningar */}
                        <div className="kyc-settings__section">
                            <h4 className="kyc-settings__section-title">Áminningar</h4>
                            <p className="kyc-settings__section-text">
                                Áminningar eru sendar sjálfvirkt úr KYC kerfinu þegar hámarks tími er liðinn frá síðustu könnun.
                                Hér er hægt að skrá netfang fyrir allar áminningar og aðrar tilkynningar sem berast úr kerfinu:
                            </p>
                            <Input
                                label="Netfang"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                fullWidth
                            />
                        </div>

                        {/* TODO: Uncomment when backend supports these settings */}
                        {/* Tíðni áminninga */}
                        {/* <div className="kyc-settings__section">
                            <h4 className="kyc-settings__section-title">Tíðni áminninga</h4>
                            <p className="kyc-settings__section-text">
                                Hér er hægt að stilla <strong>tíðni áminninga</strong> sem sendar eru þegar tími er kominn á nýja könnun.
                                <br />
                                Athugið að áhættumeiri viðskiptavinir ættu að sæta tíðari uppfærslu áreiðanleikakönnunar.
                            </p>
                            <div className="kyc-settings__frequency-rows">
                                <div className="kyc-settings__frequency-row">
                                    <span className="kyc-settings__risk-badge kyc-settings__risk-badge--low">
                                        Lítil áhætta
                                    </span>
                                    <DropSelect
                                        options={FREQUENCY_OPTIONS}
                                        defaultValue={lowRiskFrequency}
                                        onChange={(val) => setLowRiskFrequency(val as string)}
                                        size="lg"
                                        className="kyc-settings__frequency-select"
                                    />
                                </div>
                                <div className="kyc-settings__frequency-row">
                                    <span className="kyc-settings__risk-badge kyc-settings__risk-badge--medium">
                                        Meðal áhætta
                                    </span>
                                    <DropSelect
                                        options={FREQUENCY_OPTIONS}
                                        defaultValue={mediumRiskFrequency}
                                        onChange={(val) => setMediumRiskFrequency(val as string)}
                                        size="lg"
                                        className="kyc-settings__frequency-select"
                                    />
                                </div>
                                <div className="kyc-settings__frequency-row">
                                    <span className="kyc-settings__risk-badge kyc-settings__risk-badge--high">
                                        Mikil áhætta
                                    </span>
                                    <DropSelect
                                        options={FREQUENCY_OPTIONS}
                                        defaultValue={highRiskFrequency}
                                        onChange={(val) => setHighRiskFrequency(val as string)}
                                        size="lg"
                                        className="kyc-settings__frequency-select"
                                    />
                                </div>
                            </div>
                        </div> */}

                        {/* Alþjóðlegir válistar */}
                        {/* <div className="kyc-settings__section">
                            <h4 className="kyc-settings__section-title">Alþjóðlegir válistar</h4>
                            <p className="kyc-settings__section-text">
                                KYC kerfið flettir sjálfkrafa upp einstaklingum á móti alþjóðlegum válistum,
                                þvingunarlistum, PEP og neikvæðri umfjöllun (e. adverse media). Leitað er eftir
                                nafni, fæðingardegi og þjóðerni. Hér er hægt að stilla samsvörun (e. match rate)
                                niðurstaðna, það er hvaða líkindi þurfa að vera til staðar til að niðurstaða
                                teljist viðeigandi. Lágmarks samsvörun er 85%.
                            </p>
                            <div className="kyc-settings__match-rate-row">
                                <span className="kyc-settings__match-rate-label">
                                    Samsvörun (e. match rate)
                                </span>
                                <div className="kyc-settings__match-rate-input-wrapper">
                                    <Input
                                        inputType="percentage"
                                        value={matchRate}
                                        onChange={(e) => setMatchRate(e.target.value)}
                                        percentageMin={85}
                                        percentageMax={100}
                                    />
                                </div>
                            </div>
                        </div> */}

                        {/* Sjálfvirk uppfletting */}
                        {/* <div className="kyc-settings__section">
                            <h4 className="kyc-settings__section-title">Sjálfvirk uppfletting</h4>
                            <p className="kyc-settings__section-text">
                                Hér er hægt að virkja sjálfvirka uppflettingu á þeim <strong>lögaðilum</strong> sem
                                áreiðanleikakönnun er framkvæmd á. Sjálfvirk uppfletting er þá gerð framvegis,
                                samhliða öðrum uppflettingum á viðskiptavini.
                            </p>
                            <Toggle
                                toggled={autoLookupEnabled}
                                onToggle={setAutoLookupEnabled}
                                label="Virkja sjálfvirka uppflettingu á lögaðilum á móti alþjóðlegum válistum"
                            />
                            <p className="kyc-settings__section-text">
                                Athugaðu að rukkað er sérstaklega fyrir hverja uppflettingu samkvæmt{' '}
                                <Link url="/Verdskra">verðskrá Keldunnar.</Link>
                            </p>
                        </div> */}
                    </div>

                    {/* Right column */}
                    {/* TODO: Uncomment when backend supports banner upload */}
                    {/* <div className="kyc-settings__column kyc-settings__column--right">
                        <div className="kyc-settings__section">
                            <h4 className="kyc-settings__section-title">Sérsniðið útlit tölvupósta</h4>
                            <p className="kyc-settings__section-text">
                                Hér er hægt að hlaða upp sérsniðnum borða sem birtist í haus tölvupósta sem
                                sendir eru til viðskiptavina úr KYC kerfinu.
                            </p>
                            <p className="kyc-settings__section-text">
                                Hér að neðan er forskoðun á tölvupóstinum eins og hann mun líta út.
                                Borðinn birtist efst í tölvupóstinum:
                            </p>

                            <KYCSettingsEmailPreview
                                bannerSrc={bannerPreview && !bannerError ? bannerPreview.url : undefined}
                            />

                            <p className="kyc-settings__section-text">
                                Athugið að borðinn þarf að vera í eftirfarandi stærð:
                            </p>
                            <div className="kyc-settings__size-requirements">
                                <div className="kyc-settings__size-item">
                                    <span className="kyc-settings__size-bullet" />
                                    <strong>Breidd: 600 px</strong>
                                </div>
                                <div className="kyc-settings__size-item">
                                    <span className="kyc-settings__size-bullet" />
                                    <strong>Hæð: allt að 300 px</strong>
                                </div>
                                <div className="kyc-settings__size-item">
                                    <span className="kyc-settings__size-bullet" />
                                    <strong>Hámarks þyngd: 150 kb</strong>
                                </div>
                            </div>

                            <KYCSettingsBannerUpload
                                bannerPreview={bannerPreview}
                                setBannerPreview={setBannerPreview}
                                bannerError={bannerError}
                                setBannerError={setBannerError}
                                setBannerFile={setBannerFile}
                            />

                            {bannerPreview && !bannerError && (
                                <div className="kyc-settings__banner-info">
                                    <img
                                        src={bannerPreview.url}
                                        alt="Banner preview"
                                        className="kyc-settings__banner-preview-img"
                                    />
                                    <div className="kyc-settings__banner-meta">
                                        <span>{bannerPreview.name}</span>
                                        <span>{bannerPreview.width} x {bannerPreview.height} px</span>
                                        <span>{Math.round(bannerPreview.size / 1024)} kb</span>
                                        <Button
                                            buttonType="secondary"
                                            size="sm"
                                            icon={faTrashCan}
                                            onClick={handleDeleteBanner}
                                        >
                                            Eyða mynd
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {bannerError && bannerPreview && (
                                <div className="kyc-settings__banner-error-info">
                                    <div className="kyc-settings__banner-meta kyc-settings__banner-meta--error">
                                        <span>{bannerPreview.name}</span>
                                        <span>{bannerPreview.width} x {bannerPreview.height} px</span>
                                        <span>{Math.round(bannerPreview.size / 1024)} kb</span>
                                    </div>
                                    <p className="kyc-settings__banner-error-text">
                                        {bannerError}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div> */}
                </div>

                {/* Desktop save button */}
                <div className="kyc-settings__footer">
                    <Button
                        buttonType="primary"
                        onClick={handleSave}
                        showLoader={saving}
                        disabled={saving || !isDirty}
                    >
                        Vista breytingar
                    </Button>
                </div>
            </div>

            {/* Unsaved changes modal */}
            <Modal
                show={showUnsavedModal}
                onHide={() => setShowUnsavedModal(false)}
                hasCloseButton
                header="Óvistaðar breytingar"
                body={
                    <>
                        <p>Þú ert með óvistaðar breytingar. Ertu viss um að þú viljir fara til baka?</p>
                        <ul className="kyc-settings__changes-list">
                            {getChangedFields().map((field) => (
                                <li key={field.label}>
                                    <strong>{field.label}:</strong>{' '}
                                    <span className="kyc-settings__change-old">{field.oldValue}</span>
                                    {' → '}
                                    <span className="kyc-settings__change-new">{field.newValue}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="modal__button-row">
                            <Button buttonType="secondary" onClick={() => setShowUnsavedModal(false)}>
                                Hætta við
                            </Button>
                            <Button buttonType="primary" onClick={() => { setShowUnsavedModal(false); onGoBack(); }}>
                                Fara til baka
                            </Button>
                        </div>
                    </>
                }
            />

            {/* Toast notifications */}
            <ToastContainer className="toastContainer">
                <Toast onClose={() => setToastShow(false)} autohide delay={TOAST_DELAY} show={toastShow}>
                    <Alert type={toastType} headText={toastMessage} />
                </Toast>
            </ToastContainer>
        </div>
    );
};

export default KYCSettings;
